import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VoiceSelector from './components/VoiceSelector';
import TagEditor from './components/TagEditor';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import { fetchAudioUrl, playAudioUrl } from './audioUtils';
import { useTheme } from '@mui/material/styles';

// SentenceStats displays **global performance statistics** for a Sentence:
//   - It reads history/bestScore via the Sentence API (getAttemptCount,
//     getAverageScore, getMaxScore, getShortestDuration, getMaxScoreTime,
//     getMaxScorePath), which are all derived from the persisted
//     phraseVal.history and phraseVal.bestScore fields in MongoDB.
//   - It does NOT show the transient sessionAttempts/sessionBestScore that
//     VoiceRecorder keeps in memory for the current tab.
const SentenceStats = ({ sentence, isOpen, onClose, isRtlDict, sentenceCallBack, errorCallBack, allContentTags }) => {
    const theme = useTheme();
    const [audioUrl, setAudioUrl] = useState(null);
    const [playUrl, setPlayUrl] = useState('');

    useEffect(() => {
        setAudioUrl(null);
    }, [sentence]);

    const onPlayFailure = () => setAudioUrl(null);

    const playVoice = useCallback(async (gender) => {
        if (!sentence) {
            console.warn('playVoice: sentence missing');
            return;
        }
        const getter = gender === 'male' ? sentence.getMaleVoicePath : sentence.getFemaleVoicePath;
        if (typeof getter !== 'function') {
            console.warn('playVoice: missing getter for', gender);
            return;
        }
        const filename = getter.call(sentence);
        if (!filename) {
            console.warn('playVoice: no filename returned for', gender);
            return;
        }
        try {
            const url = await fetchAudioUrl(filename);
            setPlayUrl('AIVoice');
            if (url) {
                setAudioUrl(url); // record URL so UI can use it
                // Play programmatically to ensure single-tap play on iPhone Safari
                playAudioUrl(url, onPlayFailure);
            } else {
                console.error(`Signed url for ${filename} not returned`);
            }
        } catch (err) {
            console.error('Failed to fetch audio url', err);
        }
    }, [sentence]);

    const handleVolumeClick = useCallback(async () => {
        if (!sentence) {
            console.warn('handleVolumeClick: sentence missing');
            return;
        }

        if (typeof sentence.getGlobalBestScorePath !== 'function') {
            console.warn('handleVolumeClick: getGlobalBestScorePath not available on sentence');
            return;
        }

        const filename = sentence.getGlobalBestScorePath();
        if (!filename) {
            console.warn('handleVolumeClick: getBestAttemptAudioPath returned empty');
            return;
        }

        try {
            const url = await fetchAudioUrl(filename);
            setPlayUrl('BestTry');
            if (url) {
                setAudioUrl(url); // record URL so UI can use it
                playAudioUrl(url, onPlayFailure);
            } else {
                console.error(`Signed url for ${filename} not returned`);
            }
        } catch (err) {
            console.error('handleVolumeClick: Failed to fetch/play audio url', err);
        }
    }, [sentence]);

    const formatTimestamp = (s) => {
        if (s && typeof s.getGlobalBestScoreTime === 'function') {
            const time = s.getGlobalBestScoreTime();
            if (time) return format(time, 'MMMM do, yyyy H:mm:ss');
        }
        return null;
    };

    const timestamp = formatTimestamp(sentence);

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            aria-labelledby="sentence-stats-title"
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: 'seashell',
                    color: 'black',
                    borderRadius: '10px',
                    p: 1,
                    height: 600,
                },
            }}
        >
            <DialogTitle
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                dir={isRtlDict && isRtlDict.Learn ? 'rtl' : 'ltr'}
            >
                    {sentence ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isRtlDict && isRtlDict.Learn ? 'flex-end' : 'flex-start', flex: 1 }}>
                            <Typography
                                variant="body1"
                                component="div"
                                id="sentence-stats-title"
                                sx={{
                                    fontSize: '1.2rem',
                                    color: 'blue',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'normal',
                                    width: '100%',
                                    mb: 2,
                                    textAlign: isRtlDict && isRtlDict.Learn ? 'right' : 'left',
                                }}
                            >
                                {`${sentence.sentence}`}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography
                            variant="body1"
                            component="div"
                            id="sentence-stats-title"
                            sx={{ fontSize: '1rem', fontWeight: 'bold', textAlign: isRtlDict && isRtlDict.Learn ? 'right' : 'left' }}
                        >
                            Statistics
                        </Typography>
                    )}
                    <IconButton onClick={onClose} sx={{ color: 'black' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

            <DialogContent>
                <VoiceSelector onPlayMale={() => playVoice('male')} onPlayFemale={() => playVoice('female')} />

                {audioUrl && playUrl === 'AIVoice' && (
                    <Box sx={{ mt: 2 }}>
                        <audio controls src={audioUrl} onError={onPlayFailure} style={{ width: '100%' }} />
                    </Box>
                )}

                {sentence && ((sentence.contentType === 'MyContent')|| (sentence.contentType === 'conversations') ) && (
                    <TagEditor sentence={sentence} 
                               sentenceCallBack={sentenceCallBack} 
                               errorCallBack = {errorCallBack}
                               allContentTags = {allContentTags}
                               />
                )}

                {sentence && sentence.getGlobalAttemptCount && sentence.getGlobalAttemptCount() > 0 && (
                    <List sx={{ maxWidth: 350 }} dense>
                        <ListItem sx={{ py: 0.1 }}>
                            <ListItemText primary={`Number of attempts: ${sentence.getGlobalAttemptCount()}`} />
                        </ListItem>
                        <ListItem sx={{ py: 0.1 }}>
                            <ListItemText primary={`Average score: ${sentence.getGlobalAverageScore()}`} />
                        </ListItem>
                        <ListItem sx={{ py: 0.1 }}>
                            <ListItemText primary={`Max score: ${sentence.getGlobalMaxScore()}`} />
                        </ListItem>
                        <ListItem sx={{ py: 0.1 }}>
                            <ListItemText primary={`Shortest duration: ${sentence.getGlobalShortestDuration()}`} />
                        </ListItem>
                        {timestamp && (
                            <ListItem sx={{ py: 0.1 }}>
                                <ListItemText primary={`Best attempt time: ${timestamp}`} />
                            </ListItem>
                        )}
                        <ListItem>
                            <ListItemButton onClick={handleVolumeClick}>
                                <Box display="flex" alignItems="center">
                                    <VolumeUpIcon sx={{ color: 'black', mr: 1 }} />
                                    <Typography variant="body2">Play best attempt audio</Typography>
                                </Box>
                            </ListItemButton>
                        </ListItem>
                    </List>
                )}
                                    {audioUrl && playUrl === 'BestTry' && (
                                        <Box sx={{ mt: 2 }}>
                                            <audio controls src={audioUrl} onError={onPlayFailure} style={{ width: '100%' }} />
                                        </Box>
                                    )}

                {sentence &&
                    Array.isArray(sentence.explanations) &&
                    sentence.explanations.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                                Explanations:
                            </Typography>
	                                    {sentence.explanations.map((exp, idx) => {
                                            const sourceLabel = [exp.service_name, exp.model].filter(Boolean).join(' / ');

                                            return (
	                                        <Box key={idx} sx={{ mb: 1 }}>
	                                            {exp.explanationType && (
	                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
	                                                    {`${exp.explanationType}:`}
	                                                </Typography>
                                            )}
                                                {sourceLabel && (
                                                    <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                                                        {sourceLabel}
                                                    </Typography>
                                            )}
                                            <Box sx={{ mt: 0.5 }}>
	                                                <ReactMarkdown>{exp.explanation || ''}</ReactMarkdown>
	                                            </Box>
	                                        </Box>
                                            );
                                        })}
                        </Box>
                    )}

            </DialogContent>
        </Dialog>
    );
};

export default React.memo(SentenceStats);
